import React, { useState } from "react";
import {
  X,
  EyeOff,
  User,
  Briefcase,
  Calendar,
  CheckCircle,
  XCircle,
  RefreshCw,
} from "lucide-react";
import axios from "axios";
import { MyDomain } from "../../../App";
import { useTranslation } from "react-i18next";

export default function BossEmployeesNotActive({
  visible,
  onClose,
  onRefresh,
  allData,
}) {
  const { t } = useTranslation();
  const [refreshing, setRefreshing] = useState(false);
  const [activatingId, setActivatingId] = useState(null);

  // Filter inactive employees (active === 2)
  const inactiveEmployees =
    allData?.myEmployees?.filter((item) => item.active === 2) || [];

  const handleRefresh = async () => {
    setRefreshing(true);
    if (onRefresh) {
      await onRefresh();
    }
    setRefreshing(false);
  };

  const handleActivateEmployee = async (employee) => {
    if (
      !window.confirm(t("confirmActivateEmployee"))
    ) {
      return;
    }

    setActivatingId(employee.id);
    try {
      await axios.put(`${MyDomain}Employee/UpdateEmployee`, {
        Id: employee.id,
        Name: employee.name,
        Salary: employee.salary || 0,
        PassWord: employee.passWord,
        Type: employee.type,
        TypeSalary: employee.typeSalary,
        MonthlySalary: employee.monthlySalary || 0,
        Active: 1, // Activate employee
      });

      alert(t("employeeActivatedSuccessfully"));

      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error("❌ Error activating employee:", error);
      alert(t("failedToActivateEmployee"));
    } finally {
      setActivatingId(null);
    }
  };

  const getSalaryDisplay = (item) => {
    if (item.typeSalary === 2) {
      return {
        amount: item.monthlySalary || 0,
        type: t("monthly"),
        icon: Calendar,
        color: "#9C27B0",
      };
    } else {
      return {
        amount: item.salary || 0,
        type: t("daily"),
        icon: Calendar,
        color: "#4CAF50",
      };
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-6xl max-h-[90vh] flex flex-col animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <EyeOff className="w-5 h-5 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">
              {t("inactiveEmployees")}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
              title="Refresh"
            >
              <RefreshCw
                className={`w-5 h-5 text-gray-600 ${
                  refreshing ? "animate-spin" : ""
                }`}
              />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Employee Count */}
        <div className="px-6 py-3 bg-red-50 border-b border-gray-200">
          <p className="text-sm font-semibold text-red-600">
            {inactiveEmployees.length}{" "}
            {inactiveEmployees.length === 1 ? t("employee") : t("employees")}
          </p>
        </div>

        {/* Employee List */}
        <div className="flex-1 overflow-y-auto p-6">
          {inactiveEmployees.length === 0 ? (
            // Empty State
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t("noInactiveEmployees")}
              </h3>
              <p className="text-sm text-green-600">
                {t("allEmployeesActive")}
              </p>
            </div>
          ) : (
            // Employee Cards Grid
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {inactiveEmployees.map((employee) => {
                const salaryInfo = getSalaryDisplay(employee);
                const SalaryIcon = salaryInfo.icon;
                const isActivating = activatingId === employee.id;

                return (
                  <div
                    key={employee.id}
                    className="bg-white rounded-xl p-5 shadow-sm border border-red-200 hover:shadow-md transition-all"
                  >
                    {/* Avatar & Header */}
                    <div className="flex flex-col items-center mb-4">
                      <div className="w-14 h-14 bg-red-500 rounded-full flex items-center justify-center mb-3">
                        <User className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="font-semibold text-gray-900 text-center text-base line-clamp-1">
                        {employee.name}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">
                        ID: {employee.id}
                      </p>
                    </div>

                    {/* Type Badge */}
                    <div className="flex justify-center mb-3">
                      <div
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${
                          employee.type === 2
                            ? "bg-orange-50 text-orange-600"
                            : "bg-blue-50 text-blue-600"
                        }`}
                      >
                        {employee.type === 2 ? (
                          <Briefcase className="w-3.5 h-3.5" />
                        ) : (
                          <User className="w-3.5 h-3.5" />
                        )}
                        <span>
                          {employee.type === 2 ? t("manager") : t("employee")}
                        </span>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-100 mb-3"></div>

                    {/* Salary Info */}
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center gap-2 text-sm">
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: salaryInfo.color + "15" }}
                        >
                          <SalaryIcon
                            className="w-4 h-4"
                            style={{ color: salaryInfo.color }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-500">
                            {salaryInfo.type}
                          </p>
                          <p
                            className="font-bold text-sm truncate"
                            style={{ color: salaryInfo.color }}
                          >
                            ${salaryInfo.amount.toLocaleString()}
                          </p>
                        </div>
                      </div>

                      {/* Inactive Badge */}
                      <div className="flex items-center gap-2 bg-red-50 px-3 py-1.5 rounded-lg">
                        <XCircle className="w-4 h-4 text-red-600" />
                        <span className="text-xs font-semibold text-red-600">
                          {t("inactive")}
                        </span>
                      </div>
                    </div>

                    {/* Activate Button */}
                    <button
                      onClick={() => handleActivateEmployee(employee)}
                      disabled={isActivating}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm transition ${
                        isActivating
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "bg-green-100 text-green-700 border border-green-600 hover:bg-green-200"
                      }`}
                    >
                      {isActivating ? (
                        <>
                          <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                          <span>{t("activating")}</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          <span>{t("active")}</span>
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer - Close Button */}
        <div className="p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition"
          >
            {t("cancel")}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        .line-clamp-1 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 1;
        }
      `}</style>
    </div>
  );
}
